import React, { useEffect, useMemo, useState } from "react";
import PropertySelect from "./PropertySelect";
import RecipientItem from "../CampaignSteps/components/RecipientItem";
import { PropertyState, RoleOption } from "./Step3.types";
import { getOptionsByProperties } from "PhishingManager/service/index.Service";
import DebounceInput from "PhishingManager/components/DebouncedInput";
import SelectedLabel from "./SelectedLabel";
import { ROLE_DESCRIPTION } from "PhishingManager/PhishingManager.constants";

interface Props {
    loading: boolean;
    tenentId: number;
    propertyData: PropertyState;
    roleOptions: RoleOption[];
    selectedRoles: RoleOption[];
    selectedProperties: number[];
    allPropertiesSelected: boolean;
    setRoleOptions: (roles: RoleOption[]) => void;
    setSelectedRoles: (roles: RoleOption[]) => void;
    toggleLoading: (status: boolean) => void;
    setSelectedProperties: (propertyIds: number[]) => void;
    setPropertySearchQuery: (val: string | null) => void;
    moveCurrentPage: () => void;
    setAllPropertiesSelected: (val: boolean) => void;
}

const RolesRecipients: React.FC<Props> = ({
    tenentId,
    loading,
    propertyData,
    roleOptions,
    selectedRoles,
    selectedProperties,
    allPropertiesSelected,
    setRoleOptions,
    setSelectedRoles,
    toggleLoading,
    setSelectedProperties,
    setPropertySearchQuery,
    moveCurrentPage,
    setAllPropertiesSelected
}) => {
    const [search, setSearch] = useState("");

    const filteredRoles = useMemo(() => {
        return roleOptions.filter((role) => (!search || role.label.toLowerCase().includes(search.toLowerCase())));
    }, [roleOptions, search]);

    const totalUsersCount = useMemo(() => roleOptions.reduce((acc, group) => {
        const isSelected = selectedRoles.find((s) => s.value === group.value);
        return isSelected ? acc + group.count : acc;
    }, 0), [filteredRoles, selectedRoles])

    const allGroupsSelected = useMemo(() => {
        return roleOptions.length ? roleOptions.length === selectedRoles.length : false;
    }, [roleOptions, selectedRoles])

    const handleSelectAllGroups = () => {
        let data : RoleOption[] = [];
        if(!allGroupsSelected)  data = roleOptions;
        setSelectedRoles(data);
    }

    const handleGroupToggle = (groupItem: RoleOption) => {
        const isSelected = selectedRoles.some((g) => g.value === groupItem.value);
        setSelectedRoles(isSelected ? selectedRoles.filter((g) => g.value !== groupItem.value) : [...selectedRoles, groupItem]);
    };

    const getData = async () => {
        try {
            if (!tenentId) return;
            toggleLoading(true);
            const res = await getOptionsByProperties("role", Number(tenentId), selectedProperties, allPropertiesSelected);
            const grouped : RoleOption[] = Object.values(
                (res?.data || []).reduce((acc: Record<number, RoleOption>, group: any) => {
                    const key = group.id;

                    if (!acc[key]) {
                        acc[key] = {
                            value: group.id,
                            label: group.description,
                            description: ROLE_DESCRIPTION[group.description] || "",
                            count: 0,
                            propertyId: []
                        };
                    }

                    acc[key].count += group?.user_count || 0;
                    acc[key].propertyId.push(group?.property_id || 0);
                    return acc;
                }, {})
            );
            setRoleOptions(grouped);
        } catch (error) {
            setRoleOptions([]);
        } finally {
            toggleLoading(false);
        }
    };

    useEffect(() => {
        if (selectedProperties.length) getData();
        else setRoleOptions([]);
    }, [selectedProperties])

    useEffect((() => {
        if (!allPropertiesSelected) setSelectedRoles([]);
    }), [allPropertiesSelected]);

    return (
        <div className="phishing-step3__role-section">
            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                        Select property <span className="required">*</span>
                    </div>
                    <PropertySelect {...{ loading, propertyData, selectedProperties, setSelectedProperties, allSelected: allPropertiesSelected, setAllSelected: setAllPropertiesSelected, moveCurrentPage, setSearchValue: setPropertySearchQuery }}
                        onPropertyDeselect={(propertyValue) => {
                            setSelectedRoles(
                                selectedRoles.filter((group) => {
                                    const selGroup = filteredRoles.find((g) => g.value === group.value);
                                    return selGroup?.propertyId.includes(propertyValue);
                                })
                            );
                        }}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <DebounceInput placeholder="Search by role name" setDebouncedSearch={setSearch} />
                    </div>
                </div>
            </div>

            <SelectedLabel labelText={`Select a role ${filteredRoles.length > 0 ? `(${filteredRoles.length} Results)` : ''}`} totalCount={totalUsersCount}  selectedAll={allGroupsSelected} handleSelectAll={handleSelectAllGroups} />

            <div className="phishing-step3__role-list">
                {selectedProperties.length === 0 && !allPropertiesSelected ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        {`Please select a property to see roles.`}
                    </div>
                ) : filteredRoles.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        {`No roles found.`}
                    </div>
                ) : (
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                        {filteredRoles.map((group) => (
                            <RecipientItem
                                key={group.value}
                                title={group.label}
                                description={`${group.description} - ${group.count} user${group.count > 1 ? "s" : ""}`}
                                iconType={"recipientRole"}
                                isChecked={selectedRoles.some(g => g.value === group.value)}
                                onToggle={() => handleGroupToggle(group)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RolesRecipients;